// src/components/kuestion/campus/ModeSelect.jsx
//
// First sign-in, asked once (contract v3 §1.1, §11; build note §3). The
// permanence warning sits ABOVE the cards, not in help text. Continue opens a
// commit dialog that spells out the consequences and takes a typed SOLO or
// CAMPUS before the single PUT. The copy in the warning is contractual.
import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useCampus } from './CampusContext';
import { ThemeToggle } from '../shared/ThemeContext';
import AppNoticeboard from '../shared/AppNoticeboard';
import { Mark, Alert, Dialog } from '../shared/Bits';
import { WarnIcon } from '../shared/Icons';

const SOLO = 'solo';
const CAMPUS = 'campus';

const ModeCard = ({ on, onPick, title, who, lede, pluses, minus }) => (
  <button type="button" className={`kqc-modecard${on ? ' is-on' : ''}`} onClick={onPick} aria-pressed={on}>
    <span className="kqc-modecard__top">
      <span className="kqc-mode__dot" />
      <span className="kqc-modecard__title">{title}</span>
      <span className="kqc-spacer" />
      <span className="kqc-modecard__who">{who}</span>
    </span>
    <span className="kqc-modecard__lede">{lede}</span>
    <span className="kqc-modecard__rows">
      {pluses.map((p) => <span key={p}><span className="plus">+</span><span>{p}</span></span>)}
      <span className="is-minus"><span className="plus">&minus;</span><span>{minus}</span></span>
    </span>
  </button>
);

export default function ModeSelect() {
  const { user } = useAuth();
  const { chooseMode } = useCampus();
  const [pick, setPick] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isCampus = pick === CAMPUS;
  const name = isCampus ? 'Campus' : 'Solo';
  const word = isCampus ? 'CAMPUS' : 'SOLO';
  const confirmed = pick && typed.trim().toUpperCase() === word;

  const commit = async () => {
    if (!confirmed || busy) return;
    setBusy(true); setError('');
    try {
      await chooseMode(pick);
    } catch (e) {
      setError(e.message);
      setBusy(false);
      setCommitting(false);
    }
  };

  return (
    <div className="kqc kqc-wizard">
      <div className="kqc-topbar">
        <span className="kqc-topbar__mark"><Mark /></span>
        <span className="kqc-topbar__brand">Kuestion</span>
        <span className="kqc-spacer" />
        <AppNoticeboard />
        <ThemeToggle />
        <span className="tag tag-neutral">Signed in as {user?.email || user?.username}</span>
      </div>

      <div className="kqc-wizard__body">
        <div className="kqc-wizard__inner" style={{ maxWidth: 1040 }}>
          <div className="kqc-kicker" style={{ marginBottom: 14 }}>First sign-in &middot; asked once</div>
          <h1 className="kqc-h1--big" style={{ marginBottom: 16, maxWidth: '26ch' }}>How will you use Kuestion?</h1>
          <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 'clamp(22px,3vw,30px)', maxWidth: '60ch', fontSize: 'clamp(15px,1.5vw,18px)' }}>
            Two different jobs, one platform. Pick the one that describes you &mdash; the screens, the navigation and the data you keep all follow from it.
          </p>

          <div className="kqc-box kqc-box--accent" style={{ marginBottom: 'clamp(22px,3vw,32px)' }}>
            <div className="kqc-box__head kqc-box__head--accent"><WarnIcon /><span>Read this before you choose</span></div>
            <div className="kqc-box__body" style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--color-neutral-800)' }}>
              <strong className="kqc-mono" style={{ color: 'var(--color-text)', fontSize: 14 }}>This choice is permanent and cannot be changed later &mdash; in either direction.</strong>{' '}
              A Solo account can never become an examiner account, and an examiner account can never go back to Solo. If you pick wrongly, the only remedies are signing up again with a different account or contacting support. We are not able to switch it for you from this screen or any other.
            </div>
          </div>

          {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}

          <div className="kqc-modes" style={{ gap: 'clamp(18px,2.6vw,28px)', marginBottom: 'clamp(22px,3vw,32px)' }}>
            <ModeCard
              on={pick === SOLO} onPick={() => setPick(SOLO)} title="Solo" who="Just me"
              lede="I am studying for my own exams — a certification, a licence, a resit. Nobody assigns me anything."
              pluses={['My own question banks and templates', 'Timed mocks with full answers and explanations', 'My own topic strengths and score history']}
              minus="No organisation, no students, no assigning"
            />
            <ModeCard
              on={pick === CAMPUS} onPick={() => setPick(CAMPUS)} title="Campus" who="Me and my students"
              lede="I am a teacher or examiner. I set papers for other people and I need to see how they did."
              pluses={['An organisation with student logins, no email needed', 'Assign papers as proctored sittings or open practice', 'Cohort analytics and one PDF report per candidate']}
              minus="Includes everything Solo has, for your own study too"
            />
          </div>

          <div className="kqc-row kqc-row--gap">
            <button type="button" className="btn btn-primary" style={{ minHeight: 50, fontSize: 15 }} disabled={!pick} onClick={() => { setTyped(''); setCommitting(true); }}>
              {pick === SOLO ? 'Continue as Solo' : pick === CAMPUS ? 'Continue as Campus' : 'Continue'}
            </button>
            <span className="kqc-small kqc-muted" style={{ maxWidth: '44ch' }}>
              {pick ? 'You will be asked to type the word to confirm. Nothing is set until then.' : 'Pick one to continue. Nothing is set until you confirm on the next step.'}
            </span>
          </div>
        </div>
      </div>

      {committing && pick && (
        <Dialog title="Permanent choice" danger icon={<WarnIcon />} onClose={busy ? null : () => setCommitting(false)} width={540}>
          <h3>Set this account to {name}?</h3>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 14 }}>Once set, neither you nor support can change it from inside the product. Specifically:</p>
          <div className="kqc-dialog__list">
            <div>{isCampus ? 'This account will own an organisation and can never revert to Solo' : 'This account can never become an examiner account or hold students'}</div>
            <div>{isCampus ? 'You will create the organisation next — its prefix is also permanent' : 'No organisation is created, and no student can ever be added'}</div>
            <div>Changing later means <strong>signing up again with a different account</strong></div>
            <div>Your existing exams, banks and history stay with this account either way</div>
          </div>
          <div className="field" style={{ marginBottom: 18 }}>
            <label htmlFor="mode-confirm">Type <strong>{word}</strong> to confirm</label>
            <input id="mode-confirm" className="input kqc-mono" type="text" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={word} autoComplete="off" disabled={busy} autoFocus />
          </div>
          <div className="kqc-row">
            <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} disabled={!confirmed || busy} onClick={commit}>
              {busy ? 'Saving…' : `Set to ${name} — permanently`}
            </button>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => setCommitting(false)} disabled={busy}>Go back and reconsider</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
